/**
 * Colour lighten/darken helpers, pure and free of any RN imports so they can be
 * unit-tested in the node/vitest environment (importing them from MapView pulls
 * in react-native + react-native-svg).
 *
 * Both handle `#rrggbb` hex AND `rgb()/rgba()` strings. The rgba support is
 * essential: the neutral/unowned territory fill is an rgba string, and the
 * attack-target / selection fill darkens the underlying colour. Before rgba was
 * supported, darkening a neutral target was a silent no-op — it "stayed roughly
 * the same colour" — so you couldn't tell a grey neutral was being targeted.
 */

interface ParsedColor {
  r: number
  g: number
  b: number
  /** null = the source was hex (no alpha channel). */
  a: number | null
}

/** Parse `#rrggbb` or `rgb()/rgba()` into channels (+ optional alpha), else null. */
export function parseColor(color: string): ParsedColor | null {
  const hex = color.match(/^#([0-9a-f]{6})$/i)
  if (hex) {
    const h = hex[1]!
    return {
      r: parseInt(h.slice(0, 2), 16),
      g: parseInt(h.slice(2, 4), 16),
      b: parseInt(h.slice(4, 6), 16),
      a: null,
    }
  }
  const rgba = color.match(
    /^rgba?\(\s*([\d.]+)\s*,\s*([\d.]+)\s*,\s*([\d.]+)\s*(?:,\s*([\d.]+)\s*)?\)$/i,
  )
  if (rgba) {
    return {
      r: Number(rgba[1]),
      g: Number(rgba[2]),
      b: Number(rgba[3]),
      a: rgba[4] !== undefined ? Number(rgba[4]) : null,
    }
  }
  return null
}

/** Re-emit a colour in its original family (hex if no alpha, else rgba). */
export function formatColor(r: number, g: number, b: number, a: number | null): string {
  const ch = (n: number) => Math.max(0, Math.min(255, Math.round(n)))
  if (a === null) {
    const toHex = (n: number) => ch(n).toString(16).padStart(2, '0')
    return `#${toHex(r)}${toHex(g)}${toHex(b)}`
  }
  return `rgba(${ch(r)}, ${ch(g)}, ${ch(b)}, ${a})`
}

/**
 * Lighten a colour by mixing toward white. amount=0.45 reads as a pastel shade.
 * Alpha is preserved; unparseable input is returned unchanged.
 */
export function lightenHex(color: string, amount = 0.45): string {
  const c = parseColor(color)
  if (!c) return color
  const mix = (ch: number) => ch + (255 - ch) * amount
  return formatColor(mix(c.r), mix(c.g), mix(c.b), c.a)
}

/**
 * Darken a colour by blending each channel toward black. `amount` is the blend
 * factor: 0 → unchanged, 1 → black. Alpha is preserved; unparseable input is
 * returned unchanged.
 */
export function darkenHex(color: string, amount: number): string {
  const c = parseColor(color)
  if (!c) return color
  const blend = (ch: number) => ch * (1 - amount)
  return formatColor(blend(c.r), blend(c.g), blend(c.b), c.a)
}

// ─── Illumination ─────────────────────────────────────────────────────────────

/** RGB (0–255) → HSL, all components 0–1. */
function toHsl(r: number, g: number, b: number): { h: number; s: number; l: number } {
  const rn = r / 255
  const gn = g / 255
  const bn = b / 255
  const max = Math.max(rn, gn, bn)
  const min = Math.min(rn, gn, bn)
  const l = (max + min) / 2
  if (max === min) return { h: 0, s: 0, l }
  const d = max - min
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min)
  const h =
    max === rn
      ? ((gn - bn) / d + (gn < bn ? 6 : 0)) / 6
      : max === gn
        ? ((bn - rn) / d + 2) / 6
        : ((rn - gn) / d + 4) / 6
  return { h, s, l }
}

/** HSL (all 0–1) → RGB (0–255). */
function fromHsl(h: number, s: number, l: number): { r: number; g: number; b: number } {
  if (s === 0) {
    const v = l * 255
    return { r: v, g: v, b: v }
  }
  const q = l < 0.5 ? l * (1 + s) : l + s - l * s
  const p = 2 * l - q
  const channel = (t: number): number => {
    let x = t
    if (x < 0) x += 1
    if (x > 1) x -= 1
    if (x < 1 / 6) return p + (q - p) * 6 * x
    if (x < 1 / 2) return q
    if (x < 2 / 3) return p + (q - p) * (2 / 3 - x) * 6
    return p
  }
  return { r: channel(h + 1 / 3) * 255, g: channel(h) * 255, b: channel(h - 1 / 3) * 255 }
}

/** Default brightness floor for an illuminated colour (HSL lightness, 0–1). */
export const ILLUMINATE_MIN_LIGHTNESS = 0.58
/** Default saturation floor, so a near-grey pin still reads as a colour when lit. */
export const ILLUMINATE_MIN_SATURATION = 0.55

/**
 * Lift a colour until it reads as LIT, without changing which colour it is.
 *
 * Hue is preserved exactly; lightness and saturation are raised to a floor and
 * otherwise left alone. A colour already above both floors comes back unchanged.
 *
 * Why this exists rather than `lightenHex`: lightening mixes toward white, so a
 * dark pin becomes a pale wash of itself — it gets brighter but loses the
 * saturation that identified it, which is how an "illuminated" region ended up
 * looking like a grey smudge on a raster backdrop. Raising HSL lightness keeps
 * the colour saturated: `#5E4800` (a very dark olive at 18% lightness) becomes a
 * vivid gold of the same hue rather than beige.
 *
 * The saturation floor handles the other failure: a near-neutral pin like
 * `#3D4B52` (15% saturation) can be made as bright as you like and still read as
 * grey, so there would be no way to tell WHICH region just lit up.
 *
 * Alpha is preserved; unparseable input is returned unchanged.
 */
export function illuminateHex(
  color: string,
  minLightness = ILLUMINATE_MIN_LIGHTNESS,
  minSaturation = ILLUMINATE_MIN_SATURATION,
): string {
  const c = parseColor(color)
  if (!c) return color
  const { h, s, l } = toHsl(c.r, c.g, c.b)
  // A true grey (s === 0) has no hue to preserve, so saturating it would invent
  // a colour — red, as it happens, since hue defaults to 0. Brighten only.
  const nextS = s === 0 ? 0 : Math.max(s, minSaturation)
  const nextL = Math.max(l, minLightness)
  if (nextS === s && nextL === l) return color
  const rgb = fromHsl(h, nextS, nextL)
  return formatColor(rgb.r, rgb.g, rgb.b, c.a)
}

/**
 * Blend `color` toward `target` by `amount` (0 → unchanged, 1 → target).
 * `color`'s alpha is preserved. Unparseable input is returned unchanged.
 * Used to fade a colour toward the map background so it stays fully opaque
 * (no bleed-through) while reading as de-emphasised.
 */
export function mixHex(color: string, target: string, amount: number): string {
  const c = parseColor(color)
  const t = parseColor(target)
  if (!c || !t) return color
  const mix = (a: number, b: number) => a + (b - a) * amount
  return formatColor(mix(c.r, t.r), mix(c.g, t.g), mix(c.b, t.b), c.a)
}
